<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productPriceSchema.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productPriceSchema.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">{{
            $t('add_new')
          }}</ts-button>
          <div class="d-flex ts-items-center ts-space-x-3">
            <Input
              search
              v-model="search"
              :placeholder="$t('productPriceSchema.search')"
              :style="width"
              @on-focus="() => (width = 'width: 500px')"
              @on-blur="() => (width = 'width: 300px')"
            />
            <ts-filter ref="filter">
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label>{{ $t('productPriceSchema.category') }}</label>
                  <a-select
                    :allowClear="true"
                    class="ts-w-full"
                    v-model="categoryId"
                    :options="categories"
                    @change="getGroup"
                  ></a-select>
                </div>
              </div>
              <div class="row ts-mt-4">
                <div class="col-md-12">
                  <label>{{ $t('productPriceSchema.productGroup') }}</label>
                  <a-select
                    v-model="productGroupId"
                    :allowClear="true"
                    class="ts-w-full"
                    :options="groups"
                    @change="fetchData()"
                  ></a-select>
                </div>
              </div>
            </ts-filter>
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table :columns="columns" :records="resources" :loading="loading">
          <template v-slot="{ record }">
            <td>
              {{ record.barcode }}
            </td>
            <td>
              {{ record.product_name_en }}
            </td>
            <td>
              {{ record.product_name_kh }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.category_name_en }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.pro_group_name_en }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.guest_level_name_en }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.increment_type }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.increment_amount }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.created_by_name }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              {{ record.created_date }}
            </td>

            <td class="text-center ts-space-x-3 ts-whitespace-no-wrap">
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
        </ts-table>
        <div class="d-flex justify-content-end p-15">
          <ts-pagination
            v-model="pagination"
            @navigate="fetchData"
          ></ts-pagination>
        </div>
        <a-modal
          v-if="showForm"
          v-model="showForm"
          draggable
          sticky
          scrollable
          :mask="false"
          :footer="null"
          :z-index="1022"
          :title="$t('productPriceSchema.pageTitle')"
        >
          <from-action
            ref="form_action"
            @cancel="clearEdit"
            @fetchData="fetchData"
          />
        </a-modal>
      </div>
    </ts-card>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import FromAction from './edit.vue'
import { debounce } from 'lodash'

export default {
  name: 'product-price-schema',
  components: {
    FromAction
  },
  data () {
    return {
      loading: false,
      showForm: false,
      width: 'width: 300px',
      categories: [],
      groups: [],
      model: {}
    }
  },
  computed: {
    ...mapState('restaurantSale/productPriceSchema', [
      'resources',
      'pagination'
    ]),
    search: {
      get () {
        return this.$store.state.restaurantSale.productPriceSchema.search
      },
      set (newValue) {
        this.$store.commit(
          'restaurantSale/productPriceSchema/SET_SEARCH',
          newValue
        )
        this.$store.commit(
          'restaurantSale/productPriceSchema/RESET_CURRENT_PAGE'
        )
      }
    },
    categoryId: {
      get () {
        return this.$store.state.restaurantSale.productPriceSchema.category_id
      },
      set (newValue) {
        this.$store.commit(
          'restaurantSale/productPriceSchema/SET_CATEGORY_ID',
          newValue
        )
      }
    },
    productGroupId: {
      get () {
        return this.$store.state.restaurantSale.productPriceSchema.pro_group_id
      },
      set (newValue) {
        this.$store.commit(
          'restaurantSale/productPriceSchema/SET_GROUP_ID',
          newValue
        )
      }
    },
    columns () {
      return [
        {
          name: this.$t('productPriceSchema.barcode')
        },
        {
          name: this.$t('productPriceSchema.productNameEn')
        },
        {
          name: this.$t('productPriceSchema.productNameKh')
        },
        {
          name: this.$t('productPriceSchema.category'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productPriceSchema.productGroup'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productPriceSchema.tableType'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productPriceSchema.incrementType'),
          style: 'width: 8%'
        },
        {
          name: this.$t('productPriceSchema.incrementAmount'),
          style: 'text-align:center'
        },
        {
          name: this.$t('productPriceSchema.createdBy'),
          style: 'width: 8%'
        },
        {
          name: this.$t('productPriceSchema.createdDate'),
          style: 'width: 8%'
        },
        {
          name: this.$t('actions'),
          style: 'text-align:center; width:7%'
        }
      ]
    }
  },
  created () {
    this.fetchData()
    this.getCategory()
  },
  methods: {
    addNew () {
      this.$router.push({ name: 'product-price-schema-create' })
    },
    fetchData (attributes) {
      this.loading = true
      this.$store
        .dispatch('restaurantSale/productPriceSchema/fetch', attributes)
        .catch(error => {
          this.loading = false
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.loading = false
        })
    },
    getCategory () {
      this.$store
        .dispatch('restaurantSale/productPriceSchema/getCategory')
        .then(response => {
          this.categories = response.data.map(el => ({
            value: el.category_id,
            label: el.category_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.category = false
        })
    },

    getGroup () {
      if (this.categoryId !== undefined) {
        this.$store
          .dispatch('restaurantSale/productPriceSchema/getGroup')
          .then(response => {
            this.groups = response.data.map(el => ({
              value: el.pro_group_id,
              label: el.pro_group_name_en
            }))
          })
      }
      this.fetchData()
    },

    edit (record) {
      this.showForm = true
      this.$store.commit(
        'restaurantSale/productPriceSchema/SET_EDIT_DATA',
        record
      )
    },
    clearEdit () {
      this.showForm = false
      this.$store.commit('restaurantSale/productPriceSchema/SET_EDIT_DATA', {})
    },
    deleteRecord (record) {
      record._deleting = true
      this.$store
        .dispatch('restaurantSale/productPriceSchema/destroy', record.id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    notice (not) {
      this.$Notice[not.type]({
        title: 'PRODUCT PRICE SCHEMA',
        desc: not.text
      })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search })
    }, 500)
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('restaurantSale/productPriceSchema/RESET_STATE')
    next()
  }
}
</script>
